import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import LanguageSwitcher from '@components/LanguageSwitcher';
import { BackTop, Card, Steps, Divider, Drawer } from 'antd';

// 创建图标封装组件，与LandingPage中相同
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  return null;
};

const QuickStart = () => {
  const { t } = useTranslation('pages');
  const { t: tCommon } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title={t('quickStart.seo.title')}
        description={t('quickStart.seo.description')}
        keywords={t('quickStart.seo.keywords')}
        canonicalUrl="https://snapbeautify.pixzens.com/quick-start"
      />
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt={t('about.logoText')} width="32" height="32" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                {tCommon('home')}
              </Link>
              <Link to="/quick-start" className="font-medium text-purple-600 dark:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                {tCommon('quickStart')}
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                {tCommon('about')}
              </Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title={tCommon('navigation')}
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              {tCommon('home')}
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-purple-600 dark:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              {tCommon('quickStart')}
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              {tCommon('about')}
            </Link>
            <Link 
              to="/features" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Star" size={16} className="mr-1" />
              {tCommon('features')}
            </Link>
            <Link 
              to="/faq" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="HelpCircle" size={16} className="mr-1" />
              {tCommon('faq')}
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2">
              <Link 
                to="/privacy" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Shield" size={16} className="mr-1" />
                {tCommon('privacy')}
              </Link>
              <Link 
                to="/terms" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="FileText" size={16} className="mr-1" />
                {tCommon('terms')}
              </Link>
              <Link 
                to="/copyright" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Award" size={16} className="mr-1" />
                {tCommon('copyright')}
              </Link>
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <SafeIcon icon="Rocket" className="mr-3 text-purple-600 dark:text-purple-400" size={28} useFa={true} />
              {t('quickStart.title')}
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-4">{t('quickStart.updateDate')}</span>
            </h1>

            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                {t('quickStart.description')}
              </p>
              
              <div className="my-8">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="Play" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  {t('quickStart.steps.title')}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* 步骤1 */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300">
                        <div className="text-xl font-bold">1</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">{tCommon('upload')}</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Upload" className="text-blue-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      {t('quickStart.steps.step1')}
                    </p>
                  </div>
                  
                  {/* 步骤2 */}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-green-600 dark:text-green-300">
                        <div className="text-xl font-bold">2</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">{t('quickStart.stepTitles.addBackground')}</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Square" className="text-green-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      {t('quickStart.steps.step2')}
                    </p>
                  </div>
                  
                  {/* 步骤3 */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center text-amber-600 dark:text-amber-300">
                        <div className="text-xl font-bold">3</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">{t('quickStart.stepTitles.addAnnotation')}</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Edit2" className="text-amber-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      {t('quickStart.steps.step3')}
                    </p>
                  </div>
                  
                  {/* 步骤4 */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-300">
                        <div className="text-xl font-bold">4</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">{t('quickStart.stepTitles.adjustEffect')}</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Sliders" className="text-purple-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      {t('quickStart.steps.step4')}
                    </p>
                  </div>
                  
                  {/* 步骤5 */}
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center text-red-600 dark:text-red-300">
                        <div className="text-xl font-bold">5</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">{t('quickStart.stepTitles.saveShare')}</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Download" className="text-red-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      {t('quickStart.steps.step5')}
                    </p>
                  </div>
                </div>
              </div>
              
              <Divider className="my-10" />
              
              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-6">
                <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="HelpCircle" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  {t('quickStart.needMoreHelp.title')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('quickStart.needMoreHelp.description')}
                </p>
                <div className="flex items-center">
                  <SafeIcon icon="Mail" size={20} className="mr-2 text-purple-500 dark:text-purple-400" />
                  <a href="mailto:support@pixzens.com" className="text-purple-600 dark:text-purple-400 hover:underline">
                    support@pixzens.com
                  </a>
                </div>
                <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
                  {t('quickStart.needMoreHelp.replyTime')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <img src="/favicon.svg" alt={t('about.logoText')} width="24" height="24" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            
            <div className="flex space-x-8">
              <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('privacy')}</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('terms')}</Link>
              <Link to="/copyright" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('copyright')}</Link>
            </div>
            
            <div className="mt-4 md:mt-0 text-gray-400 text-sm">
              © {new Date().getFullYear()} {t('about.logoText')} | SnapBeautify
            </div>
          </div>
        </div>
      </footer>
      
      <BackTop>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg">
          <SafeIcon icon="ChevronUp" size={20} />
        </div>
      </BackTop>
    </div>
  );
};

export default QuickStart; 